import { lazy, ComponentType, LazyExoticComponent } from "react";

/**
 * Wraps React.lazy to handle chunk load errors by reloading the page.
 * This solves the issue where users have an old version loaded and try to
 * fetch chunks that no longer exist after a new deployment.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() =>
    importFunc().catch((error) => {
      // Check if this is a chunk load error
      // Different browsers and scenarios can produce different error messages
      const chunkFailedPatterns = [
        /Loading chunk [\d]+ failed/i,
        /Failed to fetch dynamically imported module/i,
        /Failed to fetch/i,
        /Importing a module script failed/i,
        /error loading dynamically imported module/i,
        /Unexpected token '<'/i,
        /Expected a JavaScript module script/i,
      ];

      const isChunkLoadError =
        chunkFailedPatterns.some(pattern => pattern.test(error?.message)) ||
        error?.name === "ChunkLoadError" ||
        (error?.name === "SyntaxError" && error?.message?.includes("Unexpected token '<'"));

      if (isChunkLoadError) {
        console.warn("Chunk load error detected. A new version may be available.", error);

        // Store that we've detected a version mismatch
        const reloadKey = "chunk-failed-reload";
        const hasReloaded = sessionStorage.getItem(reloadKey);

        if (!hasReloaded) {
          console.info("Reloading page to fetch latest version...");

          // Mark that we're about to reload to prevent infinite loops
          sessionStorage.setItem(reloadKey, "true");

          // Reload the page to get the fresh version
          window.location.reload();

          // Return a never-resolving promise to prevent the error from propagating
          // while the page reloads
          return new Promise(() => { });
        }

        // If we've already reloaded once, clear the flag and let the error propagate
        console.error("Page already reloaded once, but chunk still failed to load. This may indicate a more serious issue.");
        sessionStorage.removeItem(reloadKey);
      }

      // Re-throw the error if it's not a chunk load error or if we've already reloaded
      throw error;
    })
  );
}

